import moment from 'moment-timezone'
import { createSelector } from 'reselect'
import { cloneDeep } from 'lodash-es'
import { parseQuery } from '@calendly/http-tools'
import { getTimezone } from '@calendly/timezone-tools'
import { AMERICAN_NOTATION_COUNTRY_CODES } from '@calendly/booking-kit/constants'

import { i18n } from 'locales'
import { isConferencingLocation } from 'services/location'
import { isLocalStorageAllowedSelector } from 'selectors/gdpr'
import { currentUserSelector } from 'selectors/user'
import { timeNotationSelector as inviteeTimeNotationSelector, inviteeSelector } from 'selectors/invitee'
import {
  confirmationPageTypeSelector,
  locationConfigurationsSelector,
  isPollSelector,
  nextAvailabilitySelector,
  lastAvailabilitySelector
} from 'selectors/event_type'
import {
  eventTypeSelector,
  requestSelector,
  getCalendarAccounts,
  purchasedPackageSelector
} from 'root-api'
import { currentInviteeSelector } from 'selectors/current_invitee'

import { isEmbeddedSelector } from './embed'
import {
  initialMonthParamSelector,
  monthShiftSelector,
  daySelector,
  utcTodaySelector,
  selectedSpotsSelector
} from './calendar'

export const isPublisherSelector = createSelector(
  currentInviteeSelector,
  (invitee) => invitee?.event?.policy?.publisher
)

const timezonesSelector = ({ timezone }) => timezone

export const eventSelector = createSelector(
  (state) => state.scheduling?.event,
  currentInviteeSelector,
  (eventFromScheduling, currentInvitee) => {
    if (eventFromScheduling && Object.keys(eventFromScheduling).length !== 0) return eventFromScheduling
    return currentInvitee?.event
  }
)

export const countryCodeSelector = createSelector(
  requestSelector,
  ({ country_code: countryCode }) => countryCode
)

export const calendarAccountsLoadedSelector = ({ calendar_accounts: calendarAccounts }) => (
  calendarAccounts.loaded
)


export const schedulingSelector = ({ scheduling }) => scheduling

export const calendarAccountsSelector = createSelector(
  calendarAccountsLoadedSelector,
  (state) => state.calendar_accounts.accounts,
  getCalendarAccounts.select(),
  (loaded, accounts, fetched) => (loaded ? accounts : fetched?.data)
)

export const timezoneFromQuerySelector = ({ timezone }) => timezone.fromQuery

export const detectedTimezoneSelector = ({ timezone }) => timezone.detected

export const timezoneSelector = createSelector(
  timezonesSelector,
  eventTypeSelector,
  inviteeSelector,
  currentUserSelector,
  isPublisherSelector,
  (timezones, eventType, invitee, currentUser, isPublisher) => {
    if (timezones.selected) { return timezones.selected }
    const eventTypeTimezone = getTimezone(eventType.availability_timezone).identifier
    if (eventType.lock_timezone_enabled) {
      return eventType.owner_type === 'User'
        ? eventTypeTimezone
        : getTimezone(eventType.lock_timezone_content).identifier
    }
    if (isPublisher) { return eventTypeTimezone }

    if (!currentUser) {
      if (invitee.timezone) { return invitee.timezone }
    }

    if (timezones.fromQuery) { return timezones.fromQuery }
    if (timezones.detected) { return timezones.detected }
    return eventTypeTimezone
  }
)

export const todaySelector = createSelector(
  utcTodaySelector,
  timezoneSelector,
  (today, timezone) => today.clone().tz(timezone)
)

const preselectedMonthSelector = createSelector(
  initialMonthParamSelector,
  timezoneSelector,
  utcTodaySelector,
  lastAvailabilitySelector,
  (preselectedMonth, timezone, today, lastAvailability) => {
    if (!preselectedMonth) { return null }
    const monthMoment = moment.tz(preselectedMonth, 'YYYY-MM', timezone)
    const lastAvailabilityMoment = moment(lastAvailability).clone().tz(timezone).startOf('month')
    if (monthMoment.isBefore(today.clone().tz(timezone).startOf('month'))) {
      return null
    }
    if (lastAvailability && monthMoment.isAfter(lastAvailabilityMoment)) {
      return lastAvailabilityMoment
    }
    return monthMoment
  }
)

const startMonthSelector = createSelector(
  preselectedMonthSelector,
  nextAvailabilitySelector,
  utcTodaySelector,
  timezoneSelector,
  (preselectedMonth, nextAvailability, today, timezone) => {
    if (preselectedMonth) { return preselectedMonth }
    if (nextAvailability) {
      return moment(nextAvailability)
        .clone()
        .tz(timezone)
        .startOf('month')
    }
    return today
      .clone()
      .tz(timezone)
      .startOf('month')
  }
)

export const monthSelector = createSelector(
  startMonthSelector,
  monthShiftSelector,
  (startMonth, monthShift) => startMonth
    .clone()
    .add(monthShift, 'month')
)

const calendarsSelector = ({ calendars }) => calendars

export const calendarStatusSelector = createSelector(
  calendarsSelector,
  (calendars) => calendars.options?.calendarStatus
)

const currentCalendarSelector = createSelector(
  calendarsSelector,
  monthSelector,
  (calendars, month) => calendars[month.format('YYYY-MM-DD')] || {}
)

export const activeDayIdsSelector = createSelector(
  currentCalendarSelector,
  ({ days = [] }) => days
    .filter((day) => day.status === 'available')
    .map((day) => day.date)
)

const lastLoadedDateSelector = createSelector(
  currentCalendarSelector,
  ({ lastLoadedDate }) => lastLoadedDate
)

const startOfTodaySelector = createSelector(
  todaySelector,
  (today) => today.clone().startOf('day')
)

export const loadingProgressSelector = createSelector(
  startOfTodaySelector,
  monthSelector,
  lastLoadedDateSelector,
  (today, month, lastLoadedDate) => {
    if (!lastLoadedDate) { return 0 }

    // working with TZ-agnostic dates, except for "today"
    const monthId = month.clone().startOf('month').format('YYYY-MM-DD')
    const monthStart = moment(monthId)
      .startOf('month')
    const loadingStart = today ? moment.max(today, monthStart) : monthStart
    const loadingEnd = monthStart
      .clone()
      .endOf('month')
      .startOf('day')

    const total = loadingEnd.diff(loadingStart, 'days') + 1
    const loaded = moment(lastLoadedDate)
      .startOf('day')
      .diff(loadingStart, 'days') + 1

    return Math.round((100 * loaded) / total)
  }
)

export const receivedAtSelector = createSelector(
  currentCalendarSelector,
  ({ receivedAt }) => receivedAt
)

export const calendarSelector = ({ calendars }, monthId) => calendars[monthId]

export const selectedDayCalendarSelector = createSelector(
  daySelector,
  isPollSelector,
  calendarsSelector,
  (day, isPoll, calendars) => {
    if (!day) return null
    const monthId = day.date.clone().startOf('month').format('YYYY-MM-DD')
    return isPoll ? calendars[Object.keys(calendars).filter((key) => key !== 'options')[0]] : calendars[monthId]
  }
)

export const spotsSelector = createSelector(
  daySelector,
  selectedDayCalendarSelector,
  (day, calendar) => {
    if (!calendar) { return [] }
    const dayData = calendar.days.find((calendarDay) => calendarDay.date === day.id)
    return dayData ? dayData.spots : []
  }
)

export const timeNotationSelector = createSelector(
  countryCodeSelector,
  ({ calendar }) => calendar.timeNotation,
  inviteeTimeNotationSelector,
  (state) => isLocalStorageAllowedSelector('functional')(state),
  (countryCode, timeNotation, inviteeTimeNotation, isLocalStorageAllowed) => {
    if (timeNotation.selected) { return timeNotation.selected }
    if (timeNotation.stored && isLocalStorageAllowed) { return timeNotation.stored }
    if (inviteeTimeNotation) { return inviteeTimeNotation }
    if (AMERICAN_NOTATION_COUNTRY_CODES.includes(countryCode)) { return '12h' }
    return '24h'
  }
)

const schedulingErrorsSelector = ({ scheduling: { error } }) => (error || {}).payload || {}

const eventErrorsSelector = createSelector(
  schedulingErrorsSelector,
  (error) => error.event || {}
)

const inviteeErrorsSelector = createSelector(
  schedulingErrorsSelector,
  (error) => error.invitee || {}
)

export const inviteeNameFormatSelector = createSelector(
  purchasedPackageSelector,
  eventTypeSelector,
  (purchasedPackage, eventType) => (
    purchasedPackage ? 'combined' : eventType?.invitee_name_format
  )
)

export const inviteeFirstNameErrorsSelector = createSelector(
  inviteeErrorsSelector,
  (inviteeError) => inviteeError.first_name
)

export const inviteeLastNameErrorsSelector = createSelector(
  inviteeErrorsSelector,
  (inviteeError) => inviteeError.last_name
)

export const inviteeFullNameErrorsSelector = createSelector(
  inviteeErrorsSelector,
  (inviteeError) => inviteeError.full_name
)

export const inviteeEmailErrorsSelector = createSelector(
  inviteeErrorsSelector,
  (inviteeError) => inviteeError.email
)

export const inviteeTextReminderErrorsSelector = createSelector(
  inviteeErrorsSelector,
  (inviteeError) => inviteeError.phone_number
)

export const inviteeCanceledByErrorsSelector = createSelector(
  inviteeErrorsSelector,
  (inviteeError) => inviteeError.canceled_by
)

export const eventBaseErrorsSelector = createSelector(
  eventErrorsSelector,
  (error) => error.base
)

export const inviteeLocationErrorsSelector = createSelector(
  eventErrorsSelector,
  (error) => error.location || []
)

export const inviteePhoneNumberErrorsSelector = createSelector(
  eventErrorsSelector,
  (error) => error.phone_number || []
)

export const inviteeLocationChoiceErrorsSelector = createSelector(
  eventErrorsSelector,
  (error) => error.location_choice || []
)

export const inviteeCustomFieldsErrorsSelector = createSelector(
  schedulingErrorsSelector,
  (error) => (error || {}).event_fields || {}
)

const inviteeEventGuestEmailErrorsSelector = createSelector(
  schedulingErrorsSelector,
  (error) => {
    const emails = (error.event || {}).event_guests_emails
    if (!emails) { return {} }
    return emails[0]
  }
)

export const inviteeGuestsErrorsSelector = createSelector(
  schedulingErrorsSelector,
  inviteeEventGuestEmailErrorsSelector,
  (eventError, guestEmailsErrors) => {
    if (eventError.event == null) { return [] }
    return Array.from(new Set(Object.values(guestEmailsErrors).concat(eventError.event.event_guests || [])))
  }
)

export const inviteeInvalidGuestEmailsSelector = createSelector(
  inviteeEventGuestEmailErrorsSelector,
  (errors) => Object.keys(errors)
)

export const canceledByRequiredSelector = createSelector(
  eventSelector,
  isPublisherSelector,
  (event, isPublisher) => {
    if (event && event.guests.length === 0) { return false }
    if (isPublisher) { return false }
    return true
  }
)

export const singleLocationConfigurationSelector = createSelector(
  locationConfigurationsSelector,
  (locationConfigurations) => {
    if ((locationConfigurations || {}).length !== 1) { return null }
    return locationConfigurations[0]
  }
)

export const locationLabelSelector = createSelector(
  eventTypeSelector,
  singleLocationConfigurationSelector,
  (eventType, locationConfiguration) => {
    if ((locationConfiguration || {}).kind === 'outbound_call') {
      return i18n.t('booking.location.outbound_call.single_label')
    }

    return (eventType.location_label && eventType.location_label.trim()) || i18n.t('booking.invitee.location_label')
  }
)

export const selectedLocationConfigurationSelector = (state) => {
  const params = parseQuery(window.location.search)
  const config = cloneDeep(state.scheduling.location_configuration) || {}
  const event = currentInviteeSelector(state)?.event
  if (config.location === null) {
    if (event && event.location) {
      if (config.kind === 'outbound_call') {
        config.phone_field.value = event.location
      }

      if (config.kind === 'ask_invitee') {
        config.location = event.location
      }
    } else if (params.location) {
      const location = params.location.slice(0, 255)

      if (config.kind === 'outbound_call') {
        config.phone_field.value = location
      } else if (isConferencingLocation(config.kind)) {
        config.location = ''
      } else {
        config.location = location
      }
    }
  }
  return config
}

export const locationPhoneNumberSelector = createSelector(
  selectedLocationConfigurationSelector,
  (locationConfiguration) => locationConfiguration.phone_field.value
)

export const selectedLocationSelector = createSelector(
  selectedLocationConfigurationSelector,
  (locationConfiguration) => locationConfiguration.location || ''
)

export const nextMonthAvailableSelector = createSelector(
  lastAvailabilitySelector,
  monthSelector,
  timezoneSelector,
  (lastAvailability, month, timezone) => {
    if (!lastAvailability) { return true }

    const lastAvailabilityEnd = moment(lastAvailability).clone().tz(timezone)
    const nextMonthStart = month.clone().add(1, 'month')
    return lastAvailabilityEnd.isSameOrAfter(nextMonthStart)
  }
)

export const dateUrlParamsSelector = createSelector(
  monthSelector,
  daySelector,
  (month, day) => ({
    month: month.format('YYYY-MM'),
    date: day ? day.date.format('YYYY-MM-DD') : null
  })
)

const willRedirectSelector = createSelector(
  confirmationPageTypeSelector,
  isPublisherSelector,
  (confirmationPageType, isPublisher) => (confirmationPageType === 'external') && !isPublisher,
)

export const showRedirectHintSelector = createSelector(
  willRedirectSelector,
  isEmbeddedSelector,
  (willRedirect, isEmbedded) => willRedirect && !isEmbedded,
)

export const spotsParamSelector = () => {
  const requestParams = parseQuery(window.location.search)
  let startTimes = []
  if (requestParams.spots) {
    startTimes = requestParams.spots.split(',')
  }

  return startTimes
}

const selectedSpotsStartTimesSelector = createSelector(
  selectedSpotsSelector,
  (selectedSpots) => selectedSpots.map((spot) => spot.start_time)
)

export const groupedSelectedSpotsSelector = createSelector(
  timezoneSelector,
  spotsParamSelector,
  selectedSpotsStartTimesSelector,
  (timezone, paramsSpots, selectedSpots) => {
    const spots = selectedSpots.length ? selectedSpots : paramsSpots
    const groupedSpots = {}
    spots.forEach((spot) => {
      const spotStartTime = moment.tz(spot, timezone)
      const date = spotStartTime.format('YYYY-MM-DD')
      groupedSpots[date] = groupedSpots[date] || []
      groupedSpots[date].push(spotStartTime)
    })
    return groupedSpots
  }
)

export const spotUrlParamsSelector = createSelector(
  dateUrlParamsSelector,
  selectedSpotsSelector,
  ({ month, date }, selectedSpots) => ({
    month,
    date,
    spots: selectedSpots.length > 0 ? selectedSpots.map((spot) => spot.start_time).join(',') : null
  })
)
